import { CreationOverlay } from "../misc/CreationOverlay";
import { useMemo, useState } from "react";

import {
  CreateBase,
  Form,
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useNotify,
} from "ra-core";

import { useQueryClient } from "@tanstack/react-query";

import { format } from "date-fns";

import { ru } from "date-fns/locale";

import { Plus } from "lucide-react";

import { SaveButton } from "@/components/admin/form";

import { Button } from "@/components/ui/button";

import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { TaskFormContent } from "./TaskFormContent";
import type { Task } from "../types";

type AddTaskForDayProps = {
  day: Date;

  /**
   * undefined = текущий пользователь
   * null = не выбирать ответственного заранее
   * id = конкретный пользователь
   */
  salesId?: Identifier | null;

  display?: "icon" | "row";

  onCreated?: () => void | Promise<void>;
};

export const AddTaskForDay = ({
  day,
  salesId,
  display = "row",
  onCreated,
}: AddTaskForDayProps) => {
  const { identity } = useGetIdentity();

  const notify = useNotify();

  const dataProvider = useDataProvider();

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    [],
  );

  const initialRecord = useMemo<Partial<Task>>(() => {
    if (!identity) {
      return {};
    }

    const dueDate = new Date(day);

    dueDate.setHours(18, 0, 0, 0);

    const assignee = salesId === null ? undefined : (salesId ?? identity.id);

    return {
      type: "",

      status: "new",

      contact_id: null,

      sales_id: assignee,

      created_by_sales_id: identity.id,

      due_date: dueDate.toISOString(),
    };
  }, [day, identity, salesId]);

  if (!identity) {
    return null;
  }

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();

    setSelectedContactIds([]);

    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setSelectedContactIds([]);
    }
  };

  const handleSuccess = async (task: any) => {
    let hasLinkError = false;

    try {
      /*
       * Все выбранные люди
       * записываются в новую
       * таблицу task_contacts.
       */
      if (selectedContactIds.length > 0) {
        const results = await Promise.allSettled(
          selectedContactIds.map((contactId) =>
            dataProvider.create("task_contacts", {
              data: {
                task_id: task.id,

                contact_id: contactId,
              },
            }),
          ),
        );

        hasLinkError = results.some((result) => result.status === "rejected");
      }

      /*
       * Старое поле contact_id
       * пока сохраняем для
       * совместимости со старой
       * частью CRM.
       */
      const firstContactId = selectedContactIds[0] ?? null;

      if (
        firstContactId != null &&
        String(task.contact_id ?? "") !== String(firstContactId)
      ) {
        try {
          await dataProvider.update("tasks", {
            id: task.id,

            data: {
              contact_id: firstContactId,
            },

            previousData: task,
          });
        } catch (error) {
          console.error("Failed to update legacy contact_id", error);
        }
      }

      /*
       * Сохраняем старую
       * Atomic-логику last_seen
       * для всех выбранных людей.
       */
      if (selectedContactIds.length > 0) {
        await Promise.allSettled(
          selectedContactIds.map(async (contactId) => {
            try {
              const { data: contact } = await dataProvider.getOne("contacts", {
                id: contactId,
              });

              await dataProvider.update("contacts", {
                id: contactId,

                data: {
                  last_seen: new Date().toISOString(),
                },

                previousData: contact,
              });
            } catch (error) {
              console.error("Failed to update contact last_seen", error);
            }
          }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ["tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["task_contacts"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contact_tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });

      setOpen(false);

      setSelectedContactIds([]);

      await onCreated?.();

      if (hasLinkError) {
        notify("Задача создана, но не все контакты удалось привязать", {
          type: "warning",
        });

        return;
      }

      notify("Задача создана", {
        type: "success",
      });
    } catch (error) {
      console.error("Failed after calendar task creation", error);

      setOpen(false);

      await onCreated?.();

      notify("Задача создана, но возникла ошибка при привязке контактов", {
        type: "warning",
      });
    }
  };

  return (
    <>
      {display === "icon" ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Создать задачу"
          onClick={handleOpen}
        >
          <Plus className="h-4 w-4" />
        </Button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className="flex w-full items-center gap-3 rounded-lg border border-dashed p-3 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
            <Plus className="h-4 w-4" />
          </div>

          <span className="font-medium">Добавить задачу</span>
        </button>
      )}

      <CreateBase
        resource="tasks"
        record={initialRecord}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      >
        <CreationOverlay open={open} onOpenChange={handleOpenChange}>
          <Form className="min-h-0 flex-1 flex flex-col overflow-hidden">
            <DialogHeader className="border-b p-4 pr-12 text-left">
              <DialogTitle className="text-xl font-semibold capitalize">
                Новая задача
                {" · "}
                {format(day, "d MMMM", {
                  locale: ru,
                })}
              </DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <TaskFormContent
                selectedContactIds={selectedContactIds}
                onSelectedContactIdsChange={setSelectedContactIds}
              />
            </div>

            <DialogFooter className="border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] w-full justify-end">
              <SaveButton className="w-full h-12" />
            </DialogFooter>
          </Form>
        </CreationOverlay>
      </CreateBase>
    </>
  );
};
